import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import { contentTypesGet, contentTypesUpdate } from '@/shared/api/generated/content/content';
import type { ComputationBody, FieldBody, TypeBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Select, SelectItem } from '@/shared/ui/Select';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './Rules.module.scss';

const NONE = '—';

/** Поля типа — с вложенными полями групп. */
function useFields(typeId: string | undefined) {
  return useQuery({
    queryKey: ['content-type', typeId],
    enabled: typeId !== undefined,
    queryFn: async () => {
      const response = await contentTypesGet(typeId ?? '');

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на поля');
      }

      return response.data;
    },
    select: (data) => data.fields ?? [],
  });
}

/** Выбор поля из списка; пусто — «—», если можно не выбирать. */
function FieldSelect({
  label,
  value,
  fields,
  optional = false,
  hint,
  onChange,
}: {
  label: string;
  value: string | undefined;
  fields: FieldBody[];
  optional?: boolean;
  hint?: string | undefined;
  onChange: (key: string) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <Select
        value={value === undefined || value === '' ? NONE : value}
        onValueChange={(next) => {
          onChange(next === NONE ? '' : next);
        }}
      >
        {optional && <SelectItem value={NONE}>Не нужно</SelectItem>}
        {!optional && (value === undefined || value === '') && (
          <SelectItem value={NONE}>Выберите поле</SelectItem>
        )}
        {fields.map((f) => (
          <SelectItem key={f.key} value={f.key}>
            {f.name} · {f.key}
          </SelectItem>
        ))}
      </Select>
    </Field>
  );
}

/**
 * Правила сервера (шаг 7.5): то, что нельзя доверить сайту. «Не больше N
 * записей от посетителя» — одна попытка теста, одна заявка на мероприятие.
 * Вычисления — баллы теста по скрытым правильным ответам и сумма заказа по
 * ценам товаров: итог пишет сервер, подделать его из браузера нельзя.
 */
export function ServerRulesEditor({
  type,
  readOnly = false,
}: {
  type: TypeBody;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const scope = useSiteScope();
  const fields = useFields(type.id).data ?? [];
  const top = fields.filter((f) => f.parent_id === undefined);
  const groups = top.filter((f) => f.type === 'group');
  const numbers = top.filter((f) => f.type === 'number');
  const simple = top.filter(
    (f) => !['group', 'blocks', 'richtext', 'image', 'file'].includes(f.type),
  );

  const rules = type.server_rules;
  const [limited, setLimited] = useState(rules.per_visitor !== undefined);
  const [limit, setLimit] = useState(String(rules.per_visitor?.limit ?? 1));
  const [by, setBy] = useState(rules.per_visitor?.by?.[0] ?? '');
  const [compute, setCompute] = useState<ComputationBody[]>(rules.compute ?? []);
  const [busy, setBusy] = useState(false);

  const patch = (index: number, next: Partial<ComputationBody>) => {
    setCompute((list) => list.map((c, i) => (i === index ? { ...c, ...next } : c)));
  };

  const save = () => {
    setBusy(true);

    contentTypesUpdate(
      type.id,
      {
        server_rules: {
          ...(limited
            ? { per_visitor: { limit: Number(limit) || 1, ...(by === '' ? {} : { by: [by] }) } }
            : {}),
          compute,
        },
      },
      { 'If-Match': type.etag },
    )
      .then(() => {
        toast.success('Правила сохранены');
        void queryClient.invalidateQueries({ queryKey: ['content-type', type.id] });
        void queryClient.invalidateQueries({ queryKey: ['content-types', type.site_id] });
      })
      .catch((cause: unknown) => {
        toast.error('Не сохранилось', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className={styles['editor']}>
      <section className={styles['section']}>
        <h2 className={styles['title']}>Ограничение на посетителя</h2>
        <p className={styles['muted']}>
          Сколько записей может создать один вошедший посетитель: «одна попытка теста», «одна заявка
          на мероприятие». Без входа на сайте такие записи не создаются.
        </p>
        <Switch checked={limited} disabled={readOnly} onCheckedChange={setLimited}>
          Ограничить
        </Switch>
        {limited && (
          <div className={styles['row']}>
            <Field label="Не больше">
              <Input
                type="number"
                min={1}
                max={1000}
                value={limit}
                disabled={readOnly}
                onChange={(e) => {
                  setLimit(e.target.value);
                }}
              />
            </Field>
            <FieldSelect
              label="В пределах поля"
              value={by}
              fields={simple}
              optional
              hint="«Одна попытка на каждый тест» — поле с тестом; не нужно — на всю коллекцию."
              onChange={setBy}
            />
          </div>
        )}
      </section>

      <section className={styles['section']}>
        <h2 className={styles['title']}>Вычисления</h2>
        <p className={styles['muted']}>
          Итог по повторяющейся группе пишет сервер — с сайта это поле не задаётся и пересчитывается
          при каждой правке.
          {groups.length === 0 &&
            ' Сначала добавьте в «Поля» повторяющуюся группу: ответы или позиции заказа.'}
        </p>

        {compute.map((c, index) => (
          <ComputationCard
            key={index}
            computation={c}
            groups={groups}
            numbers={numbers}
            fields={fields}
            types={scope?.types ?? []}
            readOnly={readOnly}
            onChange={(next) => {
              patch(index, next);
            }}
            onRemove={() => {
              setCompute((list) => list.filter((_, i) => i !== index));
            }}
          />
        ))}

        {!readOnly && groups.length > 0 && compute.length < 5 && (
          <div className={styles['actions']}>
            <Button
              variant="outline"
              tone="neutral"
              iconLeft={<Plus size={15} />}
              onClick={() => {
                setCompute((list) => [
                  ...list,
                  { kind: 'score', group: groups[0]?.key ?? '', ref: '', source: '', target: '' },
                ]);
              }}
            >
              Баллы теста
            </Button>
            <Button
              variant="outline"
              tone="neutral"
              iconLeft={<Plus size={15} />}
              onClick={() => {
                setCompute((list) => [
                  ...list,
                  { kind: 'total', group: groups[0]?.key ?? '', ref: '', source: '', target: '' },
                ]);
              }}
            >
              Сумма заказа
            </Button>
          </div>
        )}
      </section>

      {!readOnly && (
        <div>
          <Button loading={busy} onClick={save}>
            Сохранить правила
          </Button>
        </div>
      )}
    </div>
  );
}

function ComputationCard({
  computation: c,
  groups,
  numbers,
  fields,
  types,
  readOnly,
  onChange,
  onRemove,
}: {
  computation: ComputationBody;
  groups: FieldBody[];
  numbers: FieldBody[];
  fields: FieldBody[];
  types: TypeBody[];
  readOnly: boolean;
  onChange: (next: Partial<ComputationBody>) => void;
  onRemove: () => void;
}) {
  const group = groups.find((g) => g.key === c.group);
  const inner = fields.filter((f) => group !== undefined && f.parent_id === group.id);
  const refs = inner.filter((f) => f.type === 'reference');
  const ref = refs.find((f) => f.key === c.ref);
  const slug =
    typeof ref?.options['collection'] === 'string' ? ref.options['collection'] : undefined;
  const other = types.find((t) => t.slug === slug);
  const otherFields = (useFields(other?.id).data ?? []).filter((f) => f.parent_id === undefined);
  const score = c.kind === 'score';

  return (
    <div className={styles['card']}>
      <div className={styles['card__head']}>
        <Segmented
          value={c.kind}
          onValueChange={(v) => {
            onChange({ kind: v as ComputationBody['kind'] });
          }}
          aria-label="Что считать"
          options={[
            { value: 'score', label: 'Баллы теста' },
            { value: 'total', label: 'Сумма заказа' },
          ]}
        />
        {!readOnly && (
          <Button
            size="sm"
            variant="ghost"
            tone="danger"
            iconOnly
            aria-label="Убрать"
            onClick={onRemove}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>
      <p className={styles['muted']}>
        {score
          ? 'Каждый ответ сравнивается с правильным у вопроса по ссылке (без учёта регистра и лишних пробелов); баллы — из поля вопроса или по 1.'
          : 'Сумма — количество в позиции × цена товара по ссылке; нет количества — 1.'}
      </p>
      <div className={styles['grid']}>
        <FieldSelect
          label={score ? 'Ответы — группа' : 'Позиции — группа'}
          value={c.group}
          fields={groups}
          onChange={(key) => {
            onChange({ group: key, ref: '', value: '' });
          }}
        />
        <FieldSelect
          label={score ? 'Ссылка на вопрос' : 'Ссылка на товар'}
          value={c.ref}
          fields={refs}
          onChange={(key) => {
            onChange({ ref: key, source: '', points: '' });
          }}
        />
        <FieldSelect
          label={score ? 'Ответ' : 'Количество'}
          value={c.value}
          fields={inner.filter((f) => f.type !== 'reference')}
          optional={!score}
          onChange={(key) => {
            onChange({ value: key });
          }}
        />
        <FieldSelect
          label={
            score
              ? `Правильный ответ — у «${other?.name ?? '…'}»`
              : `Цена — у «${other?.name ?? '…'}»`
          }
          value={c.source}
          fields={score ? otherFields : otherFields.filter((f) => f.type === 'number')}
          hint={score ? 'Сделайте его «Скрытым» — тогда сайт его не увидит.' : undefined}
          onChange={(key) => {
            onChange({ source: key });
          }}
        />
        {score && (
          <FieldSelect
            label="Баллы за вопрос"
            value={c.points}
            fields={otherFields.filter((f) => f.type === 'number')}
            optional
            onChange={(key) => {
              onChange({ points: key });
            }}
          />
        )}
        <FieldSelect
          label={score ? 'Набрано — сюда' : 'Сумма — сюда'}
          value={c.target}
          fields={numbers}
          onChange={(key) => {
            onChange({ target: key });
          }}
        />
        {score && (
          <FieldSelect
            label="Максимум — сюда"
            value={c.max}
            fields={numbers}
            optional
            onChange={(key) => {
              onChange({ max: key });
            }}
          />
        )}
      </div>
    </div>
  );
}
